import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useConfig } from "@/contexts/ConfigContext";
import { LogOut, ScrollText, Users, ShieldCheck, LayoutDashboard, UserCircle2, Handshake, Crown, Sparkles, Zap, MessageSquare, Share2, BookOpen, Home, HeartHandshake, Scale } from "lucide-react";
import NotificationsBell from "./NotificationsBell";

const BADGE_MAP = {
  pro: { icon: Zap, label: "PRO", cls: "border-emerald-400/40 text-emerald-300 bg-emerald-400/10" },
  premium: { icon: Sparkles, label: "PREMIUM", cls: "border-violet-400/40 text-violet-300 bg-violet-400/10" },
  founder: { icon: Crown, label: "FUNDADOR", cls: "border-amber-400/50 text-amber-300 bg-amber-400/15" },
};

export default function Navbar() {
  const { user, logout } = useAuth();
  const { config } = useConfig();
  const nav = useNavigate();

  const handleLogout = async () => { await logout(); nav("/"); };

  const link = ({ isActive }) =>
    `text-sm font-medium px-1 py-2 link-underline ${isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`;

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-background/75 border-b" style={{ borderColor: "hsl(var(--border))" }} data-testid="app-navbar">
      <div className="container-app flex items-center h-16 gap-6">
        <Link to={user ? "/app" : "/"} className="flex items-center gap-3" data-testid="brand-link">
          <img src="/logo.png" alt="Colega Link" className="w-9 h-9 rounded-full object-cover" />
          <div className="flex flex-col leading-none">
            <span className="font-semibold tracking-tight text-base">Colega <span className="brand-gradient-text">Link</span></span>
            <span className="label-eyebrow text-[0.6rem] mt-0.5 hidden sm:inline">Conecta · Delega · Resuelve</span>
          </div>
        </Link>
        {user && (
          <Link to="/" className="hidden md:inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground link-underline" data-testid="nav-home-public">
            <Home size={12}/> Inicio
          </Link>
        )}
        {user ? (
          <nav className="flex items-center gap-6 ml-2">
            <NavLink to="/app" end className={link} data-testid="nav-dashboard"><span className="inline-flex items-center gap-1.5"><LayoutDashboard size={14}/>Panel</span></NavLink>
            {/* Delegaciones: solo abogados + admin (mediador y solicitante NO ven delegaciones) */}
            {(user.role === "lawyer" || user.role === "admin") && (
              <NavLink to="/app/delegaciones" className={link} data-testid="nav-delegaciones"><span className="inline-flex items-center gap-1.5"><ScrollText size={14}/>Delegaciones</span></NavLink>
            )}
            {/* Directorio de mediadores: solicitantes + abogados + admin (NO mediador) */}
            {user.role !== "mediator" && (
              <NavLink to="/app/mediadores" className={link} data-testid="nav-mediadores"><span className="inline-flex items-center gap-1.5"><Users size={14}/>Mediadores</span></NavLink>
            )}
            {/* Directorio de abogados: solicitantes + mediadores + admin (NO abogado) */}
            {user.role !== "lawyer" && (
              <NavLink to="/app/abogados" className={link} data-testid="nav-abogados"><span className="inline-flex items-center gap-1.5"><Scale size={14}/>Abogados</span></NavLink>
            )}
            <NavLink to="/app/mis-mediaciones" className={link} data-testid="nav-mis-mediaciones"><span className="inline-flex items-center gap-1.5"><Handshake size={14}/>Mis Mediaciones</span></NavLink>
            {/* Foro: solo profesionales (no requester) */}
            {user.role !== "requester" && (
              <NavLink to="/app/foro" className={link} data-testid="nav-foro"><span className="inline-flex items-center gap-1.5"><MessageSquare size={14}/>Foro</span></NavLink>
            )}
            {/* Colega IA: solo lawyers premium */}
            {user.role === "lawyer" && user.plan && user.plan !== "free" && (
              <NavLink to="/app/asistente-ia" className={link} data-testid="nav-asistente-ia">
                <span className="inline-flex items-center gap-1.5 text-cyan-300">
                  <Sparkles size={14}/>Colega IA
                </span>
              </NavLink>
            )}
            {/* Biblioteca Jurídica: sólo abogados y admin (mediadores NO acceden) */}
            {(user.role === "lawyer" || user.role === "admin") && (
              <NavLink to="/app/biblioteca-juridica" className={link} data-testid="nav-biblioteca">
                <span className="inline-flex items-center gap-1.5"><BookOpen size={14}/>Biblioteca</span>
              </NavLink>
            )}
            {/* Invitar / referrals: solo profesionales */}
            {user.role !== "requester" && (
              <NavLink to="/app/invitar" className={link} data-testid="nav-invitar"><span className="inline-flex items-center gap-1.5"><Share2 size={14}/>Invitar</span></NavLink>
            )}
            <Link to="/guia" className="text-sm font-medium px-1 py-2 link-underline text-muted-foreground hover:text-foreground" data-testid="nav-guia"><span className="inline-flex items-center gap-1.5"><BookOpen size={14}/>Guía</span></Link>
            <Link to="/blog" className="text-sm font-medium px-1 py-2 link-underline text-muted-foreground hover:text-foreground" data-testid="nav-blog"><span className="inline-flex items-center gap-1.5"><BookOpen size={14}/>Blog</span></Link>
            {user.role === "admin" && (
              <NavLink to="/app/admin" className={link} data-testid="nav-admin"><span className="inline-flex items-center gap-1.5"><ShieldCheck size={14}/>Admin</span></NavLink>
            )}
          </nav>
        ) : (
          <nav className="hidden md:flex items-center gap-6 ml-2">
            <Link to="/mediacion-familiar" className="text-sm text-cyan-300 hover:text-cyan-200 link-underline inline-flex items-center gap-1" data-testid="nav-mediacion-familiar-public">
              <HeartHandshake size={13}/> Mediación Familiar
            </Link>
            <Link to="/abogados" className="text-sm text-amber-300 hover:text-amber-200 link-underline inline-flex items-center gap-1" data-testid="nav-abogados-public">
              <Scale size={13}/> Abogados
            </Link>
            <Link to="/planes" className="text-sm text-muted-foreground hover:text-foreground link-underline" data-testid="nav-pricing">Planes</Link>
            <Link to="/blog" className="text-sm text-muted-foreground hover:text-foreground link-underline inline-flex items-center gap-1" data-testid="nav-blog-public">
              <BookOpen size={13}/> Blog
            </Link>
          </nav>
        )}
        <div className="ml-auto flex items-center gap-3">
          {user ? (
            <>
              {BADGE_MAP[user.plan] && (() => {
                const B = BADGE_MAP[user.plan];
                return (
                  <Link to="/app/membresia" className={`hidden sm:inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[10px] font-bold tracking-wider ${B.cls}`} data-testid={`plan-badge-${user.plan}`}>
                    <B.icon size={11} />
                    {B.label}{user.plan === "founder" && user.founder_number ? ` N°${user.founder_number}` : ""}
                  </Link>
                );
              })()}
              {!BADGE_MAP[user.plan] && user.role !== "admin" && (
                <Link to="/planes" className="hidden sm:inline-flex text-xs text-muted-foreground hover:text-amber-300 link-underline" data-testid="upgrade-cta">
                  Ver planes
                </Link>
              )}
              <NotificationsBell />
              <Link to="/app/perfil" className="text-sm inline-flex items-center gap-2 text-muted-foreground hover:text-foreground" data-testid="nav-profile">
                <UserCircle2 size={16}/> {user.name}
              </Link>
              <button onClick={handleLogout} className="btn-ghost py-2 text-sm" data-testid="logout-button">
                <LogOut size={14} className="mr-1.5"/> Salir
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm font-medium link-underline" data-testid="login-link">Ingresar</Link>
              <Link to="/register" className="btn-primary py-2 text-sm" data-testid="register-link">
                Crear cuenta
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
